import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, UserSearch, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatPhone } from '@/lib/utils'
import { useEvento, useGerarParcelas } from '@/hooks/use-eventos'
import { useMembros } from '@/hooks/use-membros'
import { useCriarParticipante, useParticipantes } from '@/hooks/use-participantes'
import { useAuth } from '@/hooks/use-auth'
import { useAlert } from '@/hooks/use-alert'
import { ParticipantFormCard } from './evento-participante-novo/components/participant-form-card'
import { EventSummaryCard } from './evento-participante-novo/components/event-summary-card'
import { FinancePreviewCard } from './evento-participante-novo/components/finance-preview-card'
import { type ModoInclusao, type ParticipanteFormState } from './evento-participante-novo/types'
export function EventoParticipanteNovoPage() {
  const { eventoSlug } = useParams<{ eventoSlug: string }>()
  const navigate = useNavigate()
  const { perfil } = useAuth()
  const { alert } = useAlert()

  // Fetch real data from database
  const { data: evento, isLoading: isLoadingEvento } = useEvento(eventoSlug)
  const { data: membrosData, isLoading: isLoadingMembros } = useMembros()
  const { data: participantesEvento, isLoading: isLoadingParticipantes } = useParticipantes(evento?.id)
  const criarParticipante = useCriarParticipante()
  const gerarParcelas = useGerarParcelas()

  const [modo, setModo] = useState<ModoInclusao>('existente')
  const [membroSelecionadoId, setMembroSelecionadoId] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [form, setForm] = useState<ParticipanteFormState>({
    nome: '',
    email: '',
    telefone: '',
    funcao: '',
    valor: '',
    metodo: 'pix',
    status: 'pendente',
    observacoes: '',
  })

  // Map members data to expected format and mark those already in event
  const membros = useMemo(() => {
    if (!membrosData) return []

    // Get list of usuario_id already in this event
    const participantesUsuarioIds = new Set(
      (participantesEvento || [])
        .filter(p => p.usuario_id)
        .map(p => p.usuario_id)
    )

    // Map members and mark if already participating
    return membrosData.map((membro) => ({
      id: membro.id,
      nome_exibicao: membro.nome,
      telefone: membro.telefone || null,
      email: membro.email,
      ja_participando: participantesUsuarioIds.has(membro.id),
    }))
  }, [membrosData, participantesEvento])

  const membroSelecionado = useMemo(
    () => membros.find((membro) => membro.id === membroSelecionadoId) ?? null,
    [membroSelecionadoId, membros]
  )

  useEffect(() => {
    if (modo === 'existente' && membroSelecionado) {
      setForm((prev: ParticipanteFormState) => ({
        ...prev,
        nome: membroSelecionado.nome_exibicao,
        email: membroSelecionado.email || '',
        telefone: membroSelecionado.telefone || '',
      }))
    }
    if (modo === 'existente' && !membroSelecionado) {
      setForm((prev: ParticipanteFormState) => ({
        ...prev,
        nome: '',
        email: '',
        telefone: '',
      }))
    }
    if (modo === 'novo') {
      setMembroSelecionadoId('')
      setForm((prev: ParticipanteFormState) => ({
        ...prev,
        nome: '',
        email: '',
        telefone: '',
      }))
    }
  }, [modo, membroSelecionado])

  const handleInputChange = <K extends keyof ParticipanteFormState>(
    field: K,
    value: ParticipanteFormState[K]
  ) => {
    let processedValue = value
    
    // Apply phone mask if field is telefone
    if (field === 'telefone') {
      processedValue = formatPhone(value as string) as ParticipanteFormState[K]
    }
    
    setForm((prev: ParticipanteFormState) => ({
      ...prev,
      [field]: processedValue,
    }))
  }

  const destinoSlug = eventoSlug ?? evento?.slug ?? ''

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // Validation
    if (!form.nome) {
      alert.warning('Campo obrigatório', 'O nome do participante é obrigatório.')
      return
    }

    if (!form.email && !form.telefone) {
      alert.warning('Contato obrigatório', 'Informe pelo menos um meio de contato: email ou telefone.')
      return
    }

    if (!evento?.id) {
      alert.error('Evento não encontrado', 'Não foi possível localizar o evento.')
      return
    }

    if (!perfil?.id) {
      alert.error('Usuário não autenticado', 'Faça login para continuar.')
      return
    }

    // Check if member is already registered in the event
    if (modo === 'existente' && membroSelecionadoId) {
      const jaParticipando = participantesEvento?.some(p => p.usuario_id === membroSelecionadoId)
      if (jaParticipando) {
        alert.warning('Membro já cadastrado', 'Este membro já está participando deste evento.')
        return
      }
    }

    setIsSubmitting(true)
    try {
      // Prepare participant data
      const participanteData = {
        evento_id: evento.id,
        usuario_id: modo === 'existente' ? membroSelecionadoId || null : null,
        nome: form.nome,
        email: form.email || '',
        telefone: form.telefone || '',
        funcoes: form.funcao ? [form.funcao] : [],
        status: 'pendente' as const,
        observacoes: form.observacoes || null,
        is_membro: modo === 'existente',
      }

      // Create participant
      const novoParticipante = await criarParticipante.mutateAsync(participanteData)

      // Se o evento permite parcelamento, gerar as parcelas automaticamente
      if (evento.permitir_parcelamento && evento.numero_parcelas && evento.valor > 0) {
        try {
          await gerarParcelas.mutateAsync({
            participanteId: novoParticipante.id,
            valorTotal: evento.valor,
            numeroParcelas: evento.numero_parcelas,
            dataInicio: new Date(), // Primeira parcela vence hoje
          })
          console.log('Parcelas geradas com sucesso!')
        } catch (parcelasError) {
          console.error('Erro ao gerar parcelas:', parcelasError)
          // Não falha a inscrição se não conseguir gerar parcelas
          alert.warning('Aviso', 'Participante registrado, mas houve um problema ao gerar o carnê de pagamento.')
        }
      }

      alert.success('Participante registrado!', 'O participante foi adicionado ao evento com sucesso.')
      if (destinoSlug) {
        navigate(`/eventos/${destinoSlug}`)
      } else {
        navigate(-1)
      }
    } catch (error) {
      console.error('Erro ao criar participante:', error)
      alert.error('Erro ao registrar', 'Não foi possível registrar o participante. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (isLoadingEvento || isLoadingMembros || isLoadingParticipantes) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Carregando dados...</p>
        </div>
      </div>
    )
  }

  // Event not found
  if (!evento) {
    return (
      <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-6 lg:py-8 xl:px-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="h-8 w-8 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Evento não encontrado</h1>
            <p className="text-muted-foreground">Verifique o link acessado e tente novamente.</p>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-12 text-center">
          <UserSearch className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold">Não foi possível localizar o evento</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Retorne à listagem de eventos para selecionar outro evento e incluir participantes.
          </p>
          <Button onClick={() => navigate(-1)}>Voltar para Eventos</Button>
        </div>
      </div>
    )
  }

  const isEventoGratuito = evento.gratuito === true

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-6 lg:py-8 xl:px-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/eventos/${destinoSlug}`)}
            className="h-8 w-8 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Incluir participante</h1>
            <p className="text-muted-foreground">
              Associe um membro existente ou cadastre um visitante para o evento
            </p>
          </div>
        </div>
        <Badge className="w-fit bg-primary/10 text-primary">
          Evento #{evento.id.padStart(3, '0')}
        </Badge>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="grid gap-6 lg:grid-cols-3"
      >
        <div className="space-y-6 lg:col-span-2">
          <ParticipantFormCard
            modo={modo}
            onModoChange={setModo}
            membros={membros}
            membroSelecionadoId={membroSelecionadoId}
            onSelecionarMembro={setMembroSelecionadoId}
            form={form}
            onFieldChange={handleInputChange}
            onSubmit={handleSubmit}
            onCancel={() => navigate(destinoSlug ? `/eventos/${destinoSlug}` : '/eventos')}
            isSubmitting={isSubmitting}
            eventoGratuito={isEventoGratuito}
          />
        </div>

        <div className="space-y-6">
          <EventSummaryCard evento={evento} />
          {!isEventoGratuito && <FinancePreviewCard valor={form.valor} />}
        </div>
      </motion.div>
    </div>
  )
}
